/* eslint-disable no-nested-ternary */
// eslint-disable-next-line import/no-unresolved
import * as Forms from '@forms';
import axios from 'axios';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock-upgrade';
import JSZip from 'jszip';
import PropTypes from 'prop-types';
import React, { useState, useRef } from 'react';

import { Icon, UiHtml } from '@powdr/components';
import { ButtonText, FormSilverpopRequiredFields, FormTypes } from '@powdr/constants';
import { getNodeApiDomain } from '@powdr/utils';
import { useStaticData } from '@powdr/web/src/hooks/';

import { StyledStaticForms, FormStatePopup } from './styles';

const BASIN_CAPTCHA_SITE_KEY = '6Lew3SMUAAAAAJ82QoS7gqOTkRI_dhYrFy1f7Sqy';

const PopupMessages = {
  SUCCESS: {
    HEADER: 'Thank you',
    COPY: 'Your message has been successfully sent.',
  },
  ERROR: {
    HEADER: 'Error',
    COPY: 'There was an error processing your request.',
  },
};

// TODO: convert this to use modal component
export const StaticForms = ({
  formName,
  parentColorProfile = null,
  formContent = null,
  successHeader = PopupMessages.SUCCESS.HEADER,
  successSubheader = PopupMessages.SUCCESS.COPY,
  isFullWidth = false,
  additionalSubmitAction = () => {},
  passthroughFunc = () => {},
  trackingTitle = null,
}) => {
  const [isUserVerified, setUserVerified] = useState(false);
  const [isSendingForm, setSendingForm] = useState();
  const [isSubmitSuccess, setSubmitSuccess] = useState(null);
  const [hasHPEntry, setHasHPEntry] = useState(false);
  const [preventFormSubmission, setPreventFormSubmission] = useState(false);
  const formPopupRef = useRef();
  const { reCaptchaPublicKey } = useStaticData();
  const FormTemplate = (formName) ? Forms?.[`${formName?.replaceAll(' ', '')}Form`] : null;

  const checkHP = (e) => {
    e.preventDefault();
    if (e?.target?.value?.length !== 0) setHasHPEntry(true);
    else setHasHPEntry(false);
  };

  const handleCaptchaExpiry = () => {
    setUserVerified(false);
    setSendingForm(false);
  };

  const processBasin = async (
    e,
    captchaRef,
    formId,
    formDataContainsId,
    formData,
  ) => {
    e.preventDefault(); // prevent default form behavior

    const form = e.target;
    const formDataToSend = new FormData();

    // Find the field that should be used for the filename
    const filenameField = form.querySelector('[data-use-for-filename]');
    let zipFilename = 'files.zip'; // fallback

    if (filenameField) {
      // Use its value, fallback to "files" if empty
      const baseName = filenameField.value?.trim() || 'files';
      // Sanitize filename (avoid spaces, special chars)
      zipFilename = `${baseName.replace(/\s+/g, '_')}.zip`;
    }

    // Collect all file inputs
    const fileInputs = Array.from(form.elements).filter(
      (el) => el.type === 'file',
    );

    if (fileInputs.length > 0) {
      const zip = new JSZip();

      fileInputs.forEach(({ name, files, multiple }) => {
        if (!files || files.length === 0) return;

        Array.from(files).forEach((file, i) => {
          const filename = multiple
            ? `${name}_${i}_${file.name}`
            : `${name}_${file.name}`;
          zip.file(filename, file);
        });
      });

      // Generate zip blob
      const zipBlob = await zip.generateAsync({ type: 'blob' });

      // Check compressed size (5 MB limit)
      if (zipBlob.size > 5 * 1024 * 1024) {
        alert('Files must be under 5MB in total.');
        setSendingForm(false);
        setPreventFormSubmission(false);
      }

      // Add ZIP to FormData
      formDataToSend.append('files_zip', zipBlob, zipFilename);
    }

    // Add all other non-file fields
    Array.from(form.elements).forEach((element) => {
      const {
        name, type, value, checked,
      } = element;
      if (!name || type === 'file') return;

      if (type === 'checkbox' || type === 'radio') {
        if (checked) {
          if (name.endsWith('[]')) {
            const key = name.slice(0, -2); // remove []
            // collect multiple values
            const existing = formDataToSend.get(key);
            if (existing) {
              formDataToSend.set(key, `${existing}, ${value}`);
            } else {
              formDataToSend.set(key, value);
            }
          } else {
            formDataToSend.append(name, value);
          }
        }
      } else {
        formDataToSend.append(name, value);
      }
    });

    // Form ID
    const id = formDataContainsId
      ? formData[formDataContainsId]
      : formId;

    try {
      if (!preventFormSubmission) {
        await axios.post(
          `https://usebasin.com/f/${id}`,
          formDataToSend,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          },
        );

        setSendingForm(false);
        setSubmitSuccess(true);
        disableBodyScroll(formPopupRef.current);
        form.reset();
        captchaRef.current.reset();
        additionalSubmitAction();
      }
    } catch (error) {
      setSendingForm(false);
      setSubmitSuccess(false);
      disableBodyScroll(formPopupRef.current);
      console.error('Error Submitting Form', error);
    }
  };

  const processSilverpop = (
    e,
    captchaRef,
    formData,
    showModal,
  ) => {
    const nonColumnFields = ['', 'g-recaptcha-response', 'CONTACT_LIST_ID'];

    axios.post(
      `${getNodeApiDomain()}/email/contact`,
      {
        columns: Object.keys(formData).reduce((acc, key) => {
          if (!nonColumnFields.includes(key)) {
            acc[key] = formData[key];
          }
          return acc;
        }, {}),
        formName: `${formName} Form`,
        email: formData[FormSilverpopRequiredFields.EMAIL],
        contactLists: [formData.CONTACT_LIST_ID],
      },
      { headers: { 'Content-Type': 'application/json' } },
    ).then(() => {
      setSendingForm(false);
      if (showModal) {
        setSubmitSuccess(true);
        disableBodyScroll(formPopupRef.current);
      }
      e.target.reset();
      captchaRef.current.reset();
      additionalSubmitAction();
    }).catch((error) => {
      captchaRef.current.reset();
      if (showModal) {
        setSubmitSuccess(false);
        disableBodyScroll(formPopupRef.current);
      }
      setSubmitSuccess(false);
      console.error('Error Submitting Form', error);
    });
  };

  const processForm = (
    e,
    bucketId,
    formDataContainsBucketId = null,
    captchaRef,
    formType,
    additionalData,
    showModal = true,
  ) => {
    e.preventDefault();
    // setSendingForm(true);
    const formData = Object.assign(
      {},
      ...Array.from(e.target)
        .map((element) => ((element.value !== ButtonText.SUBMIT)
          ? { [element.name]: element.value }
          : null))
        .filter((i) => i),
    );

    if (formType === FormTypes.USEBASIN || !formType) {
      processBasin(e, captchaRef, bucketId, formDataContainsBucketId, formData);
    }

    if (formType === FormTypes.SILVERPOP) {
      processSilverpop(e, captchaRef, formData, showModal);
    }
  };

  const handleFormPopupClose = () => {
    enableBodyScroll(formPopupRef.current);
    setSubmitSuccess(null);
  };

  return (
    <StyledStaticForms colorProfile={parentColorProfile} $isFullWidth={isFullWidth}>
      {(formContent?.value) && <UiHtml html={formContent} colorProfile={parentColorProfile} />}
      {(FormTemplate) && (
        <FormTemplate
          processForm={processForm}
          captchaSiteKeyBasin={BASIN_CAPTCHA_SITE_KEY}
          reCaptchaPublicKey={reCaptchaPublicKey}
          setUserVerified={setUserVerified}
          isUserVerified={isUserVerified}
          isSendingForm={isSendingForm}
          handleCaptchaExpiry={handleCaptchaExpiry}
          checkHP={checkHP}
          hasHPEntry={hasHPEntry}
          buttonText={
            (isSendingForm) ? ButtonText.SENDING : (isSubmitSuccess) ? ButtonText.SENT : null
          }
          additionalSubmitAction={additionalSubmitAction}
          passthroughFunc={passthroughFunc}
          trackingTitle={trackingTitle}
        />
      )}
      {(isSubmitSuccess !== null) && (
        <FormStatePopup ref={formPopupRef}>
          <button
            className="close-button"
            type="button"
            aria-label="Close form popup"
            onClick={handleFormPopupClose}
          >
            <Icon name="ui-close" width="25" height="25" />
          </button>
          <div>
            <h2>
              {(isSubmitSuccess)
                ? (successHeader || PopupMessages.SUCCESS.HEADER) : PopupMessages.ERROR.HEADER}
            </h2>
            <p>
              {(isSubmitSuccess)
                ? (successSubheader || PopupMessages.SUCCESS.COPY) : PopupMessages.ERROR.COPY}
            </p>
          </div>
        </FormStatePopup>
      )}
    </StyledStaticForms>
  );
};

StaticForms.propTypes = {
  formName: PropTypes.string.isRequired,
  formContent: PropTypes.string,
  parentColorProfile: PropTypes.string,
  successHeader: PropTypes.string,
  successSubheader: PropTypes.string,
  additionalSubmitAction: PropTypes.func,
  passthroughFunc: PropTypes.func,
  isFullWidth: PropTypes.bool,
  trackingTitle: PropTypes.string,
};
